
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, CardMedia, Grid, TextField, Typography, Tabs, Tab, Modal, IconButton, InputAdornment } from '@mui/material';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import SearchIcon from '@mui/icons-material/Search';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useCart } from '../context/CartContext';
import { db } from '../firebase'; // import your Firebase configuration
import { collection, getDocs } from "firebase/firestore";
import { useAdmin } from '../context/AppContext';
import SkeletonLoader from '../utils/SkeletonLoader';

const Home = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const { cartItems, addToCart, removeFromCart } = useCart();
  const [menuItems, setMenuItems] = useState([]);//
  const { adminProfile } = useAdmin();
  const [loading, setLoading] = useState(true); // Add loading state

  // Function to fetch menu items from Firestore
  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "menuItems"));
      const items = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMenuItems(items);

    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setSelectedSubCategory(null); // Reset subcategory selection on tab change
  };

  const handleSearchChange = (event) => setSearchText(event.target.value);
  const clearSearch = () => setSearchText('');

  const openModal = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleAddToCart = (item) => {
    addToCart(item);
    closeModal();
  };
  useEffect(() => { }, [cartItems]);

  const itemsToShow = useMemo(() => {
    return menuItems
      .filter(item => item.name.toLowerCase().includes(searchText.toLowerCase()))
      .filter(item =>
        (selectedTab === 0 && item.category_id === 1) ||
        (selectedTab === 1 && item.category_id === 2) ||
        (selectedTab === 2 && item.category_id === 3)
      )
      .filter(item => !selectedSubCategory || item.sub_category_name === selectedSubCategory);
  }, [menuItems, searchText, selectedTab, selectedSubCategory]);

  // 2. Memoize the subcategories to avoid re-calculation on every render
  const cateringSubCategories = useMemo(() => {
    return Array.from(
      new Set(menuItems
        .filter((item) => item.category_id && item.sub_category_name)
        .filter(item =>
          (selectedTab === 0 && item.category_id === 1) ||
          (selectedTab === 1 && item.category_id === 2) ||
          (selectedTab === 2 && item.category_id === 3)
        )
        .map((item) => item.sub_category_name)
      )
    ).map((subCategory) => ({
      label: subCategory,
      icon: <LocalDrinkIcon />,
    }));
  }, [menuItems, selectedTab]);

  return (
    <Box sx={{
      padding: 2,
      textAlign: 'center', minHeight: '100vh',
      backgroundColor: '#ffffff',
      // backgroundColor: '#033845', // Updated background color
      overflowY: 'auto', // Enable scrolling for the main container

    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on mobile, row on larger screens
          alignItems: 'center',                      // Center items vertically
          justifyContent: 'center',                  // Center items horizontally
          mb: 2,
          gap: { xs: 2, sm: 3 },                     // Space between logo and search box
        }}
      >
        <CardMedia
          component="img"
          src={adminProfile?.appLogo}
          alt="App Logo"
          sx={{
            maxWidth: { xs: '70%', sm: '25%' },       // Smaller width for mobile
            height: 'auto',                           // Maintain aspect ratio
          }}
        />

        <TextField
          type="search"
          variant="outlined"
          id="outlined-search"
          placeholder="What are you looking for?"
          value={searchText}
          multiline
          rows={1}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchText ? (
                  <IconButton onClick={clearSearch}>
                    <CancelOutlinedIcon />
                  </IconButton>
                ) : (
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
            style: {
              paddingLeft: '10px',
              fontSize: '10px', // Adjust input text font size
            },
            // Add placeholder-specific styles
            inputProps: {
              style: {
                fontSize: '16px', // Adjust placeholder font size
                color: 'Black', // Optional: Placeholder text color
              },
            },
          }}
          sx={{
            width: { xs: '80%', sm: '50%' },
            backgroundColor: 'white',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px', // Apply border-radius
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid lightgray',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#c9a56a', // Add focus color
              },
            },
          }}
        />
      </Box>

      {/* Catering and Sadya Tabs */}
      <Tabs
        centered
        value={selectedTab}
        onChange={handleTabChange}
        sx={{
          mt: 2,
          mb: 2,
          border: `2px solid #c9a56a`,
          borderRadius: '8px',
          position: 'relative',
          '& .MuiTab-root': {
            flex: 1,
            fontWeight: 'bold',
            color: (theme) => theme.palette.primary.main,
            position: 'relative', // Required for positioning borders for each tab
            '&:first-of-type': {  // CATERING Tab
              borderRight: '2px solid #c9a56a', // Vertical border on the right side for CATERING
            },
            '&:last-of-type': {  // EVENTS Tab
              borderLeft: '2px solid #c9a56a', // Vertical border on the left side for EVENTS
            },
            '&:not(:first-of-type):not(:last-of-type)': { // For the SADYA Tab (middle tab)
              borderLeft: 'none',
              borderRight: 'none',
            },
          },
          '& .Mui-selected': {
            color: '#FFFFFF !important',
            backgroundColor: (theme) => theme.palette.primary.main,
          },
        }}
        TabIndicatorProps={{
          style: { display: 'none' },
        }}
      >
        {['CATERING', 'SADYA', 'EVENTS'].map((category, index) => (
          <Tab key={index} label={category} />
        ))}
      </Tabs>

      <>
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            mb: 4,
            px: 0.1,
            gap: 0.1,
          }}
        >
          {cateringSubCategories.map((category, index) => (
            <Box
              key={index}
              sx={{
                minWidth: 100,
                maxWidth: 100,
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px solid #ddd',
                borderRadius: 2,
                boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
                padding: 1,
                flexShrink: 0,
                backgroundColor: 'transparent', // set transparent background for the container
              }}
            >
              <Button
                variant="text"
                startIcon={category.icon}
                onClick={() => setSelectedSubCategory(category.label)}
                disableRipple={true}
                disableTouchRipple
                color='transparent'
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  color: selectedSubCategory === category.label ? '#c9a56a' : '#333',
                  fontWeight:selectedSubCategory === category.label?"bold":"500",                  padding: 0,
                  minWidth: 0,
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'transparent', // prevent background on hover
                  },
                  '&:active': {
                    backgroundColor: 'transparent', // prevent background on active state
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent', // prevent background on selected state
                  }
                }}
              >
                {category.label}
              </Button>
            </Box>
          ))}
        </Box>
        {/* Display the selected label below the options */}
        {selectedSubCategory && (
          <Typography variant="subtitle1" sx={{ textAlign: 'left', fontWeight: "700", color: '#1a1a1a', fontSize: 18, mt: 1 }}>
            {selectedSubCategory}
          </Typography>
        )}
      </>
      {/* // )} */}

      {loading ? (
        <SkeletonLoader count={8} />
      ) : (
        <Grid container spacing={2}>
          {itemsToShow.map((item) => (
            <Grid item xs={6} sm={4} md={3} key={item.product_id}>
              <Box textAlign="center" sx={{
                padding: '12px',
                position: 'relative',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}>
                <CardMedia
                  onClick={() => openModal(item)}
                  component="img"
                  image={item.image}
                  alt={item.name}
                  sx={{
                    width: '100%',
                    height: 180,
                    borderRadius: 2,
                    objectFit: 'cover',
                  }}
                />
                <Typography style={{ color: "gray", fontSize: 14 }} variant="subtitle1">{item.name}</Typography>

                {cartItems.some(cartItem => cartItem.product_id === item.product_id) ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ gap: 1, padding: '8px', borderRadius: '4px' }}
                  >
                    <Typography variant="caption" color="success.main">Added</Typography>
                    <IconButton color="secondary" onClick={() => removeFromCart(item.product_id)}>
                      <CancelOutlinedIcon />
                    </IconButton>
                  </Box>

                ) : (
                  <Button variant="contained" color="primary"
                    sx={{ backgroundColor: 'primary', '&:hover': { backgroundColor: '#8B0000' } }}
                    onClick={() => openModal(item)}
                  >
                    Add to Cart
                  </Button>
                )}

              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modal for previewing item */}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 1, // Ensures 8px margin for all devices
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            padding: 2,
            borderRadius: 2,
            textAlign: 'center',
            boxShadow: 24,
            width: '90%',   // Adjust width for responsive design
            maxWidth: 400,  // Ensures max width on larger devices
            position: 'relative', // For positioning the close button
          }}
        >
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={closeModal}
            sx={{
              position: 'absolute',
              top: 1,
              right: 1,
              zIndex: 10,
              backgroundColor: 'white', // Light highlight background
              color: 'secondary.main',              // Icon color
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)', // Slightly darker on hover
              },
              borderRadius: '50%', // Circular shape
              width: 32,           // Consistent size
              height: 32,
              boxShadow: 5,
            }}
          >
            <CancelOutlinedIcon />
          </IconButton>


          {/* Content */}
          {selectedItem && (
            <>
              <Box
                component="img"
                src={selectedItem.image}
                alt={selectedItem.name}
                sx={{
                  width: '100%',          // Full width
                  maxHeight: 400,         // Set a max height
                  borderRadius: 2,
                  objectFit: 'contain',
                  transition: 'transform 0.3s ease-in-out', // Smooth zoom animation
                  '&:hover': {
                    transform: 'scale(1.1)', // Zoom effect on hover
                  },
                }}
              />
              <Typography variant="h6" sx={{ mt: 2 }}>
                {selectedItem.name}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  mt: 2,
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: '#8B0000' },
                }}
                onClick={() => handleAddToCart(selectedItem)}
              >
                Confirm to Add to Cart
              </Button>
            </>
          )}
        </Box>
      </Modal>

    </Box>
  );
};

export default Home;



